// @flow

import React, { useState, useEffect } from 'react';
import * as R from 'ramda';
import classNames from 'classnames';
import { log } from '../../utils/jsUtils';
import { useSelector, useDispatch } from 'react-redux';

import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import Fab from '@mui/material/Fab';

import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import SendIcon from '@mui/icons-material/Send';

import RemovableModalImage from '../SharedComponents/RemovableModalImage';
import ImageFileDrop from '../SharedComponents/ImageFileDrop';
import ForwardRef from '../SharedComponents/ForwardRef';
import { calculateCreditsForMsg, submitSendMessage } from '../../models/Message';
import withStyles from '../../utils/withStyles';
import styles from './Styles/SendReplyBox.Style';

import { selectBillingState } from '../../reduxStore/modules/billing/selectors';
import { selectAccountUsage } from '../../reduxStore/modules/user/selectors';
import { setRechargePopup, setUpgradePopup } from '../../reduxStore/modules/drawer/actions';
import { setSnackMessage } from '../../reduxStore/modules/common';

import type { Tenant } from '../../services/SPBackend';

type Props = {
  classes: Object,
  tenant: Tenant,
  triggerAction: Function,
};

// eslint-disable-next-line react/no-multi-comp
const SendReplyBox = (props: Props) => {
  const {
    classes,
    tenant,
    triggerAction
  } = props;
  
  const dispatch = useDispatch();
  
  // Get data from Redux store
  const billing = useSelector(selectBillingState);
  const accountUsage = useSelector(selectAccountUsage);

  const errorMsgInitState = {
    function: 'upgrade', // 'upgrade'|'recharge'|''
    msg: ''
  };

  const [message, setMessage] = useState('');
  const [selectedTagOptions, setSelectedTagOptions] = useState([]);
  const [toTenantsIds, setToTenantsIds] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [errorMsg, setErrorMsg] = useState(errorMsgInitState);
  const [creditsUsed, setCreditsUsed] = useState(0);

  useEffect(() => {
    if (typeof message !== 'undefined' && message !== null) {
      setCreditsUsed(calculateCreditsForMsg({message: message, attachment: attachment}));
    }
  }, [message]);

  const handleSetAttachment = (attachmentObj) => {
    log('[handleSetAttachment] attachmentObj: ', attachmentObj);
    setAttachment(attachmentObj);
  };

  const emptyForm = () => {
    setMessage('');
    setSelectedTagOptions([]);
    setToTenantsIds([]);
    // TODO: Finish refactoring ErrorMsg everywhere
    setErrorMsg(errorMsgInitState);
    setAttachment(null);
    setCreditsUsed(0);
  };
  
  const emptyErrorMsg = () => {
    setErrorMsg(errorMsgInitState);
    setError('');
    setIsSending(false);
  };

  const openUpgradePlan = () => {
    log('[openUpgradePlan]...');
    setIsSending(false);
    dispatch(setUpgradePopup(true));
  };

  const removeAttachment = (attachment) => (e) => {
    e.stopPropagation();
    setAttachment(null);
  };

  const handleImageError = () => {
    setAttachment(null);
    dispatch(setSnackMessage({
      message: 'Image failed to load. Please try uploading again.',
      level: 'error',
      duration: 5000
    }));
  };

  const openRechargeAccount = () => {
    log('[openRechargeAccount]...');
    setIsSending(false);
    dispatch(setRechargePopup(true));
  };

  return (
    <div>
    {
      tenant
      ? <form
          autoComplete='off'
          className={classNames(
            classes.dialogFormCtr
          )}
          noValidate
          onSubmit={e => {
            e.preventDefault();

            const _props = {
              message,
              attachments: [attachment],
              setErrorMsg,
              setIsSending,
              toTenantsIds: [tenant._id],
              // setOpenDialog: setOpen,
              triggerAction,
              emptyForm,
              billing,
              accountUsage,
              dispatch
            };
            log('[sendPM] _props: ', _props);
            submitSendMessage(_props);


            // setIsSending(true);
            // if (tenant !== null && message !== '') {
            //   triggerAction(
            //     tenant,
            //     {
            //       message,
            //       attachment
            //     },
            //     () => {
            //       log('[triggerAction] done!');
            //       emptyForm();
            //       setIsSending(false);
            //     },
            //     (errorMsg) => {
            //       setIsSending(false);
            //       setErrorMsg(errorMsg);
            //     });
            // } else {
            //   setError('Cannot send an empty message.');
            //   setIsSending(false);
            // } 
          }}
        >
        {
              attachment
              ? <RemovableModalImage
                src={attachment?.Url}
                alt="Attached image"
                onRemove={removeAttachment(attachment)}
                onError={handleImageError}
                />
              : null
            }
          <div 
          className={classNames(classes.grow, classes.flexContainer)}
          >
          <div className={classes.error}>{ error }</div>
            <div id='SendReplyBox'>
            <OutlinedInput
              sx={{ borderRadius: 24 }}
              autoFocus
              endAdornment={
                <InputAdornment position="end">
                  {
                    process.env.REACT_APP_CHARS_NB_PER_CREDIT
                    ? <Tooltip 
                      aria-label={`${creditsUsed} ${creditsUsed > 1 ? 'Credits' : 'Credit'}`}
                      placement='top-end'
                      style={{height: 32}}
                      title={`${creditsUsed} ${creditsUsed > 1 ? 'Credits' : 'Credit'}`}
                      >
                      <ForwardRef>
                      {
                        errorMsg && errorMsg.msg
                        ? <div className={classNames(classes.errorMessagesCtr,
                            errorMsg.function !== '' ? 'two-lines' : 'one-line')}
                          >
                            <div className={classNames(
                              !attachment ? classes.textFieldCaptionLeft : classes.textFieldCaptionRight2, 
                              classes.warning)} 
                              dangerouslySetInnerHTML={{__html: errorMsg.msg}}
                            />
                            {
                              errorMsg.function === 'upgrade'
                              ? <div className={classNames(
                                !attachment ? classes.textFieldCaptionLeft : classes.textFieldCaptionRight2, 
                                classes.warning)}
                                >
                                <a className={classes.clickable}
                                  onClick={openRechargeAccount}
                                >Click here</a> to activate your account.
                              </div>
                              : null
                            }
                            {
                              errorMsg.function === 'recharge'
                              ? <div className={classNames(
                                !attachment ? classes.textFieldCaptionLeft : classes.textFieldCaptionRight2, 
                                classes.warning)}
                                > 
                                <a className={classes.clickable}
                                  onClick={openRechargeAccount}
                                >Click here</a> to recharge your account.</div>
                              : null
                            }
                          </div>
                      : <div>&nbsp;</div>}
                      </ForwardRef>
                    </Tooltip>
                    : null
                  }
                  <Button
                    color='primary'
                    disabled={isSending}
                    type='submit'
                    variant='contained'
                  >
                    <SendIcon />
                  </Button>
                  
                </InputAdornment>
              }
              fullWidth
              id="message"
              multiline
              onChange={e => { 
                                setMessage(e.target.value);
                                emptyErrorMsg();
                                setError('');
                              }}
              placeholder={'Send a reply'}
              startAdornment={
                <InputAdornment >
                  <ImageFileDrop onImageReceived={handleSetAttachment}>
                    <Tooltip 
                      aria-label={'Attach an image'}
                      placement='top'
                      title={'Attach an image'}
                    >
                      
                        <ImageOutlinedIcon className={classes.actionAttachImgBtn}/>
                    </Tooltip>
                  </ImageFileDrop>
                </InputAdornment>
              }
                  type={'text'}
              value={message}
            />
            </div>
            
            
          </div>
        </form>
      : null
    }
    </div>
  );
};

export default withStyles(styles)(SendReplyBox);
